import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faBook, faUsers, faEllipsis, faPlug } from '@fortawesome/free-solid-svg-icons';
import colors, { fonts } from '../theme';

function Sidebar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }
    }, []);

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path);

    const menuItems = [
        { path: '/dashboard', icon: faChartLine, label: 'Dashboard' },
        { path: '/library', icon: faBook, label: 'Library' },
    ];

    // Admin-only menu items
    const adminMenuItems = [
        { path: '/users', icon: faUsers, label: 'Team' },
        { path: '/integrations', icon: faPlug, label: 'Integrations' }
    ];

    return (
        <div style={{
            width: '70px',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            backgroundColor: colors.white,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 200
            // borderRight removed - now using separate div below
        }}>
            {/* Logo at top */}
            <div style={{
                width: '100%',
                height: '64px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                //borderBottom: `1px solid ${colors.gray[200]}`
            }}>
                <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                }}
                onClick={() => navigate('/dashboard')}
                >
                    <img
                        src="/logo192.png"
                        alt="Miblio"
                        style={{
                            width: '40px',
                            height: '40px'
                        }}
                    />
                </div>
            </div>

            {/* Border that starts below TopBar (64px) */}
            <div style={{
                position: 'absolute',
                right: 0,
                top: '64px',
                bottom: 0,
                width: '1px',
                backgroundColor: colors.gray[200]
            }} />

            {/* Menu items */}
            <div style={{
                width: '100%',
                padding: '24px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
            }}>
                {/* Regular menu items */}
                {menuItems.map((item) => (
                    <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        style={{
                            width: '100%',
                            padding: '8px 0',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '2px',
                            transition: 'all 0.2s ease',
                            position: 'relative'
                        }}
                    >
                        {/* Purple indicator bar for active item */}
                        {isActive(item.path) && (
                            <div style={{
                                position: 'absolute',
                                left: '0px',
                                top: '38%',
                                transform: 'translateY(-50%)',
                                width: '5px',
                                height: '22px',
                                backgroundColor: colors.accent3,
                                borderRadius: '2px'
                            }} />
                        )}
                        
                        {/* Icon with rounded square background */}
                        <div 
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.3rem',
                                backgroundColor: isActive(item.path) 
                                    ? `${colors.accent3}15`
                                    : colors.white,
                                color: isActive(item.path) ? colors.accent3 : colors.gray[600],
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                if (!isActive(item.path)) {
                                    e.currentTarget.style.backgroundColor = colors.gray[200];
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!isActive(item.path)) {
                                    e.currentTarget.style.backgroundColor = colors.white;
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={item.icon} />
                        </div>
                        
                        {/* Label below icon */}
                        <span style={{
                            fontSize: '0.65rem',
                            fontWeight: isActive(item.path) ? '800' : '600',
                            color: isActive(item.path) ? colors.accent3 : colors.gray[600],
                            transition: 'color 0.2s ease',
                            fontFamily: fonts.secondary,
                            letterSpacing: '0px'
                        }}>
                            {item.label}
                        </span>
                    </button>
                ))}

                {/* Admin-only menu items */}
                {user?.role === 'admin' && adminMenuItems.map((item) => (
                    <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        style={{
                            width: '100%',
                            padding: '8px 0',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '2px',
                            transition: 'all 0.2s ease',
                            position: 'relative'
                        }}
                    >
                        {/* Purple indicator bar for active item */}
                        {isActive(item.path) && (
                            <div style={{
                                position: 'absolute',
                                left: '0px',
                                top: '38%',
                                transform: 'translateY(-50%)',
                                width: '5px',
                                height: '22px',
                                backgroundColor: colors.accent3,
                                borderRadius: '2px'
                            }} />
                        )}
                        
                        {/* Icon with rounded square background */}
                        <div 
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.3rem',
                                backgroundColor: isActive(item.path) 
                                    ? `${colors.accent3}15`
                                    : colors.white,
                                color: isActive(item.path) ? colors.accent3 : colors.gray[600],
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                if (!isActive(item.path)) {
                                    e.currentTarget.style.backgroundColor = colors.gray[200];
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!isActive(item.path)) {
                                    e.currentTarget.style.backgroundColor = colors.white;
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={item.icon} />
                        </div>
                        
                        {/* Label below icon */}
                        <span style={{
                            fontSize: '0.65rem',
                            fontWeight: isActive(item.path) ? '800' : '600',
                            color: isActive(item.path) ? colors.accent3 : colors.gray[600],
                            transition: 'color 0.2s ease',
                            fontFamily: fonts.secondary,
                            letterSpacing: '0px'
                        }}>
                            {item.label}
                        </span>
                    </button>
                ))}

                {/* More menu item at bottom */}
                <button
                    onClick={() => navigate('/more')}
                    style={{
                        width: '100%',
                        padding: '8px 0',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '2px',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        marginTop: 'auto'
                    }}
                >
                    {isActive('/more') && (
                        <div style={{
                            position: 'absolute',
                            left: '0px',
                            top: '38%',
                            transform: 'translateY(-50%)',
                            width: '5px',
                            height: '22px',
                            backgroundColor: colors.accent3,
                            borderRadius: '2px'
                        }} />
                    )}
                    
                    <div 
                        style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.3rem',
                            backgroundColor: isActive('/more') 
                                ? `${colors.accent3}15`
                                : colors.white,
                            color: isActive('/more') ? colors.accent3 : colors.gray[600],
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            if (!isActive('/more')) {
                                e.currentTarget.style.backgroundColor = colors.gray[200];
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isActive('/more')) {
                                e.currentTarget.style.backgroundColor = colors.white;
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faEllipsis} />
                    </div>
                    
                    <span style={{
                        fontSize: '0.65rem',
                        fontWeight: isActive('/more') ? '800' : '600',
                        color: isActive('/more') ? colors.accent3 : colors.gray[600],
                        transition: 'color 0.2s ease',
                        fontFamily: fonts.secondary,
                        letterSpacing: '0px'
                    }}>
                        More
                    </span>
                </button>
            </div>
        </div>
    );
}

export default Sidebar;